import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, Send, ArrowLeft, Eye, EyeOff, ExternalLink, Mail, MailCheck, Search, Users, Tag, Calendar, Clock, CheckCircle2, Smartphone, Tablet, Monitor } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import api from '../utils/api';
import Sidebar from '../components/Sidebar';
import BlockEditor from '../components/BlockEditor';
import PreviewRenderer from '../components/PreviewRenderer';

const NewsletterEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [newsletterData, setNewsletterData] = useState({
    title: '',
    subject: '',
    blocks: [],
    status: 'draft',
    showReadInBrowser: true,  // Default to showing "Read in Browser" link
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [recipientEmails, setRecipientEmails] = useState('');
  const [previewMode, setPreviewMode] = useState(false);
  // Mobile / tablet width preview for the inline Quick Preview pane.
  // Newsletters render in email clients (tables, no media queries), so a
  // width-constrained wrapper is a faithful enough preview without needing
  // an iframe + backend preview-token round-trip. 'auto' = panel width.
  const [previewViewport, setPreviewViewport] = useState('auto');
  
  // Test email state
  const [showTestEmailDialog, setShowTestEmailDialog] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testLanguage, setTestLanguage] = useState('en');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  
  // Improved send dialog state
  const [groups, setGroups] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [sendToGroups, setSendToGroups] = useState([]);
  const [sendToSubscribers, setSendToSubscribers] = useState([]);
  const [subscriberSearchSend, setSubscriberSearchSend] = useState('');
  const [sendMode, setSendMode] = useState('now');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!isNew) {
      fetchNewsletter();
    }
  }, [id]);

  const fetchNewsletter = async () => {
    try {
      const response = await api.get(`/newsletters/${id}`);
      // Ensure blocks array exists to prevent white screen errors
      const data = response.data;
      if (!data.blocks) {
        data.blocks = [];
      }
      setNewsletterData(data);
    } catch (error) {
      toast.error('Failed to load newsletter');
      navigate('/newsletters');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchGroups = async () => {
    try {
      const response = await api.get('/subscriber-groups');
      setGroups(response.data || []);
    } catch (error) {
      console.error('Failed to load groups:', error);
    }
  };
  
  const fetchSubscribers = async () => {
    try {
      const response = await api.get('/subscribers');
      setSubscribers(response.data.subscribers || []);
    } catch (error) {
      console.error('Failed to load subscribers:', error);
    }
  };
  
  const getGroupNames = (groupIds) => {
    if (!groupIds || groupIds.length === 0) return [];
    return groupIds.map(gid => {
      const group = groups.find(g => g.group_id === gid);
      return group ? group.name : null;
    }).filter(Boolean);
  };
  
  const openSendDialog = () => {
    setSendToGroups([]);
    setSendToSubscribers([]);
    setRecipientEmails('');
    setSubscriberSearchSend('');
    setSendMode('now');
    setScheduleDate('');
    setScheduleTime('');
    setShowSendDialog(true);
    // Fetch groups and subscribers
    fetchGroups();
    fetchSubscribers();
  };

  const handleSave = async () => {
    if (!newsletterData.title || !newsletterData.subject) {
      toast.error('Please enter title and subject');
      return;
    }

    setSaving(true);
    try {
      if (isNew) {
        const response = await api.post('/newsletters', newsletterData);
        toast.success('Newsletter created successfully');
        navigate(`/newsletters/${response.data.newsletter_id}`);
      } else {
        await api.put(`/newsletters/${id}`, newsletterData);
        toast.success('Newsletter updated successfully');
      }
    } catch (error) {
      toast.error('Failed to save newsletter');
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    // Get emails from selected individual subscribers
    const subscriberEmails = sendToSubscribers.map(subId => {
      const sub = subscribers.find(s => s.subscriber_id === subId);
      return sub?.email;
    }).filter(Boolean);
    
    // Get custom emails
    const customEmails = recipientEmails.split(/[,\n]/).map(e => e.trim()).filter(e => e && e.includes('@'));
    
    // Combine all emails
    const allEmails = [...new Set([...subscriberEmails, ...customEmails])];
    
    if (sendToGroups.length === 0 && allEmails.length === 0) {
      toast.error('Please select groups, subscribers, or enter email addresses');
      return;
    }
    
    // Validate schedule if scheduling
    let scheduledAt = null;
    if (sendMode === 'schedule') {
      if (!scheduleDate || !scheduleTime) {
        toast.error('Please select both date and time for scheduling');
        return;
      }
      
      const scheduledDateTime = new Date(`${scheduleDate}T${scheduleTime}`);
      if (scheduledDateTime <= new Date()) {
        toast.error('Scheduled time must be in the future');
        return;
      }
      scheduledAt = scheduledDateTime.toISOString();
    }
    
    setSending(true);
    try {
      const payload = {
        recipient_emails: allEmails,
        group_ids: sendToGroups
      };
      
      if (scheduledAt) {
        payload.scheduled_at = scheduledAt;
      }
      
      await api.post(`/newsletters/${id}/send`, payload);
      
      // Calculate total recipients
      const groupSubscriberCount = sendToGroups.reduce((acc, gid) => {
        const group = groups.find(g => g.group_id === gid);
        return acc + (group?.subscriber_count || 0);
      }, 0);
      const totalRecipients = groupSubscriberCount + allEmails.length;
      
      if (scheduledAt) {
        toast.success(`Newsletter scheduled for ${new Date(scheduledAt).toLocaleString()} (${totalRecipients} recipients)`);
      } else {
        toast.success(`Newsletter is being sent to ${totalRecipients} recipients!`);
      }
      setShowSendDialog(false);
      setRecipientEmails('');
      fetchNewsletter(); // Refresh to update sent status
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send newsletter');
    } finally {
      setSending(false);
    }
  };
  
  const handleSendTestEmail = async () => {
    if (!testEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    
    // Save newsletter first if it has unsaved changes
    if (isNew) {
      toast.error('Please save the newsletter first');
      return;
    }
    
    setSendingTestEmail(true);
    try {
      await api.post(`/newsletters/${id}/send`, { 
        recipient_emails: [testEmail.trim()],
        test_language: testLanguage !== 'en' ? testLanguage : undefined  // Only send if not English (default)
      });
      const langLabel = testLanguage !== 'en' ? ` (translated to ${testLanguage.toUpperCase()})` : '';
      toast.success(`Test email sent to ${testEmail}${langLabel}`);
      setShowTestEmailDialog(false);
      setTestEmail('');
      setTestLanguage('en');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTestEmail(false);
    }
  };

  // Open full email preview in new window
  const openFullPreview = () => {
    const previewWindow = window.open('', `newsletter_preview_${Date.now()}`);
    
    if (previewWindow) {
      previewWindow.document.open();
      const previewHtml = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Email Preview: ${newsletterData.subject || 'Newsletter'}</title>
            <style>
              * { box-sizing: border-box; margin: 0; padding: 0; }
              body { 
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                background-color: #f1f5f9;
                padding: 20px;
              }
              .email-container {
                max-width: 600px;
                margin: 0 auto;
                background: white;
                border-radius: 8px;
                box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
                overflow: hidden;
              }
              .email-header {
                background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                color: white;
                padding: 24px;
              }
              .email-header h1 {
                font-size: 14px;
                font-weight: 500;
                opacity: 0.9;
                margin-bottom: 8px;
              }
              .email-header h2 {
                font-size: 22px;
                font-weight: 700;
              }
              .email-body {
                padding: 32px 24px;
              }
              .email-body h1, .email-body h2, .email-body h3 {
                margin: 20px 0 10px 0;
                color: #1e293b;
              }
              .email-body p {
                margin: 10px 0;
                line-height: 1.7;
                color: #475569;
              }
              .email-body img {
                max-width: 100%;
                height: auto;
                margin: 20px 0;
                border-radius: 8px;
              }
              .email-body blockquote {
                border-left: 4px solid #10b981;
                padding-left: 20px;
                margin: 20px 0;
                font-style: italic;
                color: #64748b;
              }
              .email-body ul, .email-body ol {
                margin: 10px 0;
                padding-left: 30px;
              }
              .email-body li {
                margin: 5px 0;
                color: #475569;
              }
              .email-footer {
                background: #f8fafc;
                padding: 20px 24px;
                text-align: center;
                border-top: 1px solid #e2e8f0;
              }
              .email-footer p {
                font-size: 12px;
                color: #94a3b8;
              }
              .toolbar {
                position: fixed;
                top: 20px;
                right: 20px;
                background: white;
                padding: 12px 16px;
                border-radius: 8px;
                box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                font-size: 13px;
                color: #64748b;
              }
            </style>
          </head>
          <body>
            <div class="toolbar">
              📧 Email Preview Mode | Width: 600px
            </div>
            <div class="email-container">
              ${newsletterData.showReadInBrowser !== false ? `
              <div style="text-align: center; padding: 10px 0; margin-bottom: 20px; border-bottom: 1px solid #e2e8f0;">
                <a href="${isNew ? '#' : `${process.env.REACT_APP_BACKEND_URL}/api/public/newsletter/${id}/view`}" 
                   target="_blank"
                   style="color: #64748b; font-size: 12px; text-decoration: underline;">
                  Having trouble viewing this email? Read in browser
                </a>
                ${isNew ? '<span style="display: block; font-size: 10px; color: #94a3b8; margin-top: 4px;">(Save newsletter first to enable this link)</span>' : ''}
              </div>
              ` : ''}
              <div class="email-header">
                <h1>Subject Line</h1>
                <h2>${newsletterData.subject || 'No subject'}</h2>
              </div>
              <div class="email-body" id="email-content">
                ${blocksToEmailHtml(newsletterData.blocks || [])}
              </div>
              <div class="email-footer">
                <p>This is a preview of how your newsletter will appear in email clients.</p>
                <p style="margin-top: 8px;">© ${new Date().getFullYear()} Your Company. All rights reserved.</p>
              </div>
            </div>
          </body>
        </html>
      `;
      previewWindow.document.write(previewHtml);
      previewWindow.document.close();
    }
  };

  // Convert blocks to email-safe HTML
  const blocksToEmailHtml = (blocks) => {
    if (!blocks || blocks.length === 0) {
      return '<p style="color: #94a3b8; text-align: center; padding: 40px;">No content yet. Add blocks to see preview.</p>';
    }

    // Filter out hidden/unpublished blocks
    const visibleBlocks = blocks.filter(block => block.isPublished !== false);
    
    if (visibleBlocks.length === 0) {
      return '<p style="color: #94a3b8; text-align: center; padding: 40px;">All blocks are hidden. Unhide some blocks to see preview.</p>';
    }

    return visibleBlocks.map(block => {
      const { type, content = {} } = block;
      
      switch (type) {
        case 'heading':
          const level = content.level || 2;
          const text = content.html || content.text || '';
          const cleanText = text.replace(/<[^>]*>/g, '');
          return `<h${level} style="margin: 20px 0 10px 0; color: #1e293b;">${cleanText}</h${level}>`;
        
        case 'paragraph': {
          const pBtn = content.blockButton || {};
          let pBtnHtml = '';
          if (pBtn.enabled && pBtn.text) {
            const bColor   = pBtn.color || '#6366f1';
            const bTxtCol  = pBtn.textColor || '#ffffff';
            const bAlign   = pBtn.align || 'center';
            const bFull    = pBtn.fullWidth ? 'width: 100%; display: block; box-sizing: border-box;' : 'display: inline-block;';
            const bRadius  = pBtn.radius === 'pill' ? '9999px' : pBtn.radius === 'square' ? '0px' : '6px';
            const bPad     = { sm: '8px 20px', md: '12px 30px', lg: '16px 40px' }[pBtn.size || 'md'] || '12px 30px';
            const bTarget  = pBtn.openInNewTab ? ' target="_blank" rel="noopener noreferrer"' : '';
            const bMargin  = pBtn.marginTop || 16;
            pBtnHtml = `<div style="margin-top:${bMargin}px;text-align:${bAlign};"><a href="${pBtn.url || '#'}"${bTarget} style="${bFull}background-color:${bColor};color:${bTxtCol};padding:${bPad};border-radius:${bRadius};text-decoration:none;font-weight:600;text-align:center;">${pBtn.text}</a></div>`;
          }
          return `<div style="margin: 10px 0; line-height: 1.7; color: #475569;">${content.html || content.text || ''}</div>${pBtnHtml}`;
        }
        
        case 'image':
          const imgUrl = content.url || content.imageUrl;
          if (!imgUrl) return '';
          return `
            <div style="margin: 20px 0; text-align: center;">
              <img src="${imgUrl}" alt="${content.alt || content.imageAlt || ''}" style="max-width: 100%; height: auto; border-radius: 8px;"/>
              ${content.caption ? `<p style="font-size: 14px; color: #94a3b8; margin-top: 8px;">${content.caption}</p>` : ''}
            </div>
          `;
        
        case 'quote':
          return `<blockquote style="border-left: 4px solid #10b981; padding-left: 20px; margin: 20px 0; font-style: italic; color: #64748b;">${content.text || content.html || ''}</blockquote>`;
        
        case 'list':
          const items = content.items || [];
          return `
            <ul style="margin: 10px 0; padding-left: 30px;">
              ${items.map(item => `<li style="margin: 5px 0; color: #475569;">${item}</li>`).join('')}
            </ul>
          `;
        
        case 'code':
          return `<pre style="background: #1e293b; color: #e2e8f0; padding: 16px; border-radius: 8px; overflow-x: auto; font-family: monospace; font-size: 14px;">${content.code || ''}</pre>`;
        
        case 'two-column':
          return `
            <table width="100%" cellpadding="0" cellspacing="0" style="margin: 20px 0;">
              <tr>
                <td width="48%" valign="top" style="padding-right: 2%;">${content.leftHtml || ''}</td>
                <td width="48%" valign="top" style="padding-left: 2%;">${content.rightHtml || ''}</td>
              </tr>
            </table>
          `;
        
        case 'image-text-row':
          const rowImgUrl = content.imageUrl || '';
          const rowText = content.textHtml || '<p>Text content</p>';
          const imgLeft = content.imagePosition === 'left';
          if (imgLeft) {
            return `
              <table width="100%" cellpadding="0" cellspacing="0" style="margin: 20px 0;">
                <tr>
                  <td width="48%" valign="top" style="padding-right: 2%;">
                    ${rowImgUrl ? `<img src="${rowImgUrl}" alt="${content.imageAlt || ''}" style="max-width: 100%; height: auto; border-radius: 8px;"/>` : ''}
                  </td>
                  <td width="48%" valign="top" style="padding-left: 2%;">${rowText}</td>
                </tr>
              </table>
            `;
          } else {
            return `
              <table width="100%" cellpadding="0" cellspacing="0" style="margin: 20px 0;">
                <tr>
                  <td width="48%" valign="top" style="padding-right: 2%;">${rowText}</td>
                  <td width="48%" valign="top" style="padding-left: 2%;">
                    ${rowImgUrl ? `<img src="${rowImgUrl}" alt="${content.imageAlt || ''}" style="max-width: 100%; height: auto; border-radius: 8px;"/>` : ''}
                  </td>
                </tr>
              </table>
            `;
          }
        
        case 'image-text-stack':
          const stackImgUrl = content.imageUrl || '';
          const stackText = content.textHtml || '<p>Text content</p>';
          const imgTop = content.imagePosition === 'top';
          if (imgTop) {
            return `
              <div style="margin: 20px 0;">
                ${stackImgUrl ? `<div style="text-align: center; margin-bottom: 16px;"><img src="${stackImgUrl}" alt="${content.imageAlt || ''}" style="max-width: 100%; height: auto; border-radius: 8px;"/></div>` : ''}
                <div style="line-height: 1.7; color: #475569;">${stackText}</div>
              </div>
            `;
          } else {
            return `
              <div style="margin: 20px 0;">
                <div style="line-height: 1.7; color: #475569;">${stackText}</div>
                ${stackImgUrl ? `<div style="text-align: center; margin-top: 16px;"><img src="${stackImgUrl}" alt="${content.imageAlt || ''}" style="max-width: 100%; height: auto; border-radius: 8px;"/></div>` : ''}
              </div>
            `;
          }
        
        case 'card-grid':
        case 'columns': {
          const cols = content.columns || [];
          if (cols.length === 0) return '';
          const colWidth = Math.floor(100 / cols.length);
          const renderColBtn = (col) => {
            if (!col.hasButton || !col.buttonText) return '';
            const bc    = col.buttonColor || '#6366f1';
            const btc   = col.buttonTextColor || '#ffffff';
            const bst   = col.buttonStyle || 'solid';
            const bpad  = { sm: '6px 16px', md: '10px 24px', lg: '14px 32px' }[col.buttonSize || 'md'] || '10px 24px';
            const baln  = col.buttonAlign || 'left';
            const bfw   = col.buttonFullWidth ? 'width: 100%; display: block; box-sizing: border-box;' : 'display: inline-block;';
            const burl  = col.buttonUrl || '#';
            const btgt  = col.buttonOpenInNewTab ? ' target="_blank" rel="noopener noreferrer"' : '';
            const bg    = bst === 'outline' ? 'transparent' : bc;
            const fc    = bst === 'outline' ? bc : btc;
            const bdr   = bst === 'outline' ? `2px solid ${bc}` : 'none';
            return `<div style="margin-top:12px;text-align:${baln};"><a href="${burl}"${btgt} style="${bfw}background-color:${bg};color:${fc};border:${bdr};padding:${bpad};border-radius:6px;text-decoration:none;font-weight:600;text-align:center;">${col.buttonText}</a></div>`;
          };
          return `
            <table width="100%" cellpadding="0" cellspacing="0" style="margin: 20px 0;">
              <tr>
                ${cols.map((col) => `
                  <td width="${colWidth}%" valign="top" style="padding: 0 8px;">
                    ${col.imageUrl ? `<img src="${col.imageUrl}" alt="" style="max-width:100%;height:auto;border-radius:8px;margin-bottom:8px;"/>` : ''}
                    ${col.html || col.text || ''}
                    ${renderColBtn(col)}
                  </td>
                `).join('')}
              </tr>
            </table>
          `;
        }
        
        case 'button': {
          const btnText        = content.text || 'Click Here';
          const btnUrl         = content.url || '#';
          const btnColor       = content.color || '#6366f1';
          const btnTextColor   = content.textColor || '#ffffff';
          const btnAlign       = content.align || 'center';
          const btnFullWidth   = content.fullWidth || false;
          const btnRadius      = content.borderRadius === 'pill' ? '9999px' : content.borderRadius === 'square' ? '0px' : '6px';
          const btnTarget      = content.openInNewTab ? ' target="_blank" rel="noopener noreferrer"' : '';
          const sizeMap        = { sm: '8px 20px', md: '12px 30px', lg: '16px 40px', xl: '20px 48px' };
          const btnPadding     = sizeMap[content.size] || '12px 30px';
          const btnWidth       = btnFullWidth ? 'width: 100%; display: block; box-sizing: border-box;' : 'display: inline-block;';
          return `
            <div style="margin: 20px 0; text-align: ${btnAlign};">
              <a href="${btnUrl}"${btnTarget} style="${btnWidth} background-color: ${btnColor}; color: ${btnTextColor}; text-decoration: none; padding: ${btnPadding}; border-radius: ${btnRadius}; font-weight: 600; text-align: center; cursor: pointer;">${btnText}</a>
            </div>
          `;
        }

        case 'hero':
          // Get image from slides array (new format) or direct properties (legacy)
          const heroSlides = content.slides || [];
          const firstSlide = heroSlides[0] || {};
          const heroImg = firstSlide.imageUrl ?? content.imageUrl ?? content.backgroundImage ?? '';
          // Use nullish coalescing to allow empty strings
          const heroHeadline = firstSlide.headline !== undefined ? firstSlide.headline : (content.headline !== undefined ? content.headline : (content.title || ''));
          const heroSubheadline = firstSlide.subheadline !== undefined ? firstSlide.subheadline : (content.subheadline !== undefined ? content.subheadline : (content.subtitle || ''));
          const heroButtonText = firstSlide.buttonText !== undefined ? firstSlide.buttonText : (content.buttonText !== undefined ? content.buttonText : '');
          const heroButtonUrl = firstSlide.buttonUrl || content.buttonUrl || '#';
          const heroUseColor = content.useBackgroundColor || content.backgroundType === 'color';
          const heroBgColor = firstSlide.backgroundColor || content.backgroundColor || '#6366f1';
          
          // Check if there's any text content to show
          const hasHeroContent = heroHeadline || heroSubheadline || heroButtonText;
          
          // Build background style
          let heroBackground = heroUseColor 
            ? `background-color: ${heroBgColor};` 
            : (heroImg ? `background-image: url('${heroImg}'); background-size: cover; background-position: center;` : 'background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);');
          
          return `
            <div style="margin: 20px 0; text-align: center; padding: 60px 20px; ${heroBackground} border-radius: 8px;">
              ${hasHeroContent ? `
              <div style="background: rgba(0,0,0,0.5); padding: 40px; border-radius: 8px; display: inline-block; max-width: 600px;">
                ${heroHeadline ? `<h1 style="color: white; font-size: 32px; margin: 0 0 16px 0; font-weight: bold;">${heroHeadline}</h1>` : ''}
                ${heroSubheadline ? `<p style="color: rgba(255,255,255,0.9); font-size: 18px; margin: 0 0 20px 0;">${heroSubheadline}</p>` : ''}
                ${heroButtonText ? `<a href="${heroButtonUrl}" style="display: inline-block; background: white; color: #1e293b; padding: 12px 24px; border-radius: 6px; text-decoration: none; font-weight: 600;">${heroButtonText}</a>` : ''}
              </div>
              ` : ''}
            </div>
          `;
        
        case 'article-card':
          const articleTitle = content.articleTitle || 'Article Title';
          const articleExcerpt = content.articleExcerpt || '';
          const articleImage = content.articleImage || '';
          const articleDate = content.articleDate || '';
          const displayStyle = content.displayStyle || 'card';
          const showArticleImage = content.showImage !== false;
          const showArticleExcerpt = content.showExcerpt !== false;
          const showArticleDate = content.showDate !== false;
          const showReadMore = content.showReadMore !== false;
          const readMoreText = content.readMoreText || 'Read Full Article';
          
          if (displayStyle === 'headline') {
            return `
              <div style="margin: 20px 0; padding: 20px; border-left: 4px solid #6366f1;">
                <h2 style="margin: 0 0 8px 0; color: #1e293b; font-size: 24px;">${articleTitle}</h2>
                ${showArticleDate && articleDate ? `<p style="margin: 0; color: #64748b; font-size: 14px;">${articleDate}</p>` : ''}
              </div>
            `;
        } else if (displayStyle === 'full') {
            return `
              <div style="margin: 20px 0;">
                ${showArticleImage && articleImage ? `<a href="${content.articleSlug ? '#' : '#'}"><img src="${articleImage}" alt="${articleTitle}" style="max-width: 100%; height: auto; border-radius: 8px; margin-bottom: 16px;"/></a>` : ''}
                <h2 style="margin: 0 0 12px 0; color: #1e293b; font-size: 24px;">${articleTitle}</h2>
                ${showArticleDate && articleDate ? `<p style="margin: 0 0 12px 0; color: #64748b; font-size: 14px;">${articleDate}</p>` : ''}
                ${showArticleExcerpt && articleExcerpt ? `<p style="margin: 0 0 16px 0; line-height: 1.6; color: #475569;">${articleExcerpt}</p>` : ''}
                <div style="margin: 16px 0; padding: 12px 16px; background-color: #f0f9ff; border-left: 3px solid #6366f1; border-radius: 4px;">
                  <p style="margin: 0; font-size: 13px; color: #6366f1; font-style: italic;">Full article body (including buttons and blocks) will appear in the sent email.</p>
                </div>
                ${showReadMore ? `<p style="margin: 16px 0 0 0;"><a href="#" style="color: #6366f1; text-decoration: none; font-weight: 600; font-size: 14px;">${readMoreText} →</a></p>` : ''}
              </div>
            `;
          } else {
            // Card style (default)
            return `
              <div style="margin: 20px 0; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden;">
                ${showArticleImage && articleImage ? `<img src="${articleImage}" alt="${articleTitle}" style="width: 100%; height: 200px; object-fit: cover;"/>` : ''}
                <div style="padding: 20px;">
                  <h3 style="margin: 0 0 8px 0; color: #1e293b; font-size: 20px;">${articleTitle}</h3>
                  ${showArticleDate && articleDate ? `<p style="margin: 0 0 8px 0; color: #64748b; font-size: 12px;">${articleDate}</p>` : ''}
                  ${showArticleExcerpt && articleExcerpt ? `<p style="margin: 0 0 16px 0; line-height: 1.5; color: #475569; font-size: 14px;">${articleExcerpt}</p>` : ''}
                  ${showReadMore ? `<a href="#" style="color: #6366f1; text-decoration: none; font-weight: 600; font-size: 14px;">${readMoreText} →</a>` : ''}
                </div>
              </div>
            `;
          }
        
        case 'newsletter-header':
          const headerLogo = content.logoUrl || '';
          const headerLogoWidth = content.logoWidth || 150;
          const headerShowLogo = content.showLogo !== false;
          const headerBgColor = content.backgroundColor || '#6366f1';
          const headerTextColor = content.textColor || '#ffffff';
          const headerAlignment = content.alignment || 'center';
          const headerLinks = content.links || [];
          
          let linksHtml = headerLinks.map((link, i) => {
            const separator = i < headerLinks.length - 1 
              ? `<span style="color: ${headerTextColor}; margin: 0 12px; opacity: 0.5;">|</span>` 
              : '';
            return `<a href="${link.url || '#'}" style="color: ${headerTextColor}; text-decoration: none; font-weight: 500; font-size: 14px;">${link.text || ''}</a>${separator}`;
          }).join('');
          
          return `
            <div style="background-color: ${headerBgColor}; padding: 20px; text-align: ${headerAlignment}; border-radius: 8px; margin: 0 0 20px 0;">
              ${headerShowLogo && headerLogo ? `<img src="${headerLogo}" alt="Logo" style="width: ${headerLogoWidth}px; max-width: 100%; ${headerAlignment === 'center' ? 'display: block; margin: 0 auto 12px;' : 'margin-bottom: 12px;'}"/>` : ''}
              <div>${linksHtml}</div>
            </div>
          `;
        
        default:
          return '';
      }
    }).join('\n');
  };

  if (loading) {
    return (
      <div className="flex">
        <Sidebar />
        <div className="flex-1 bg-white p-8 flex items-center justify-center">
          <p className="text-slate-500">Loading newsletter...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex" data-testid="newsletter-editor">
      <Sidebar />
      
      <div className="flex-1 min-w-0 bg-white">
        {/* Header */}
        <div className="border-b border-slate-200 px-8 py-4 bg-white sticky top-0 z-10">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-4 flex-1">
              <Button variant="ghost" onClick={() => navigate('/newsletters')} data-testid="back-button">
                <ArrowLeft size={20} />
              </Button>
              <div className="flex-1 max-w-2xl">
                <Input
                  value={newsletterData.title || ''}
                  onChange={(e) => setNewsletterData({ ...newsletterData, title: e.target.value })}
                  placeholder="Newsletter title..."
                  className="text-2xl font-semibold border-none p-0 focus:ring-0 mb-2"
                  data-testid="newsletter-title-input"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                />
                <div className="flex items-center gap-2">
                  <Mail size={14} className="text-slate-400" />
                  <Input
                    value={newsletterData.subject || ''}
                    onChange={(e) => setNewsletterData({ ...newsletterData, subject: e.target.value })}
                    placeholder="Email subject line..."
                    className="text-base border-none p-0 focus:ring-0 flex-1"
                    data-testid="newsletter-subject-input"
                  />
                </div>
                
                {/* Read in Browser Toggle */}
                <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-100">
                  <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newsletterData.showReadInBrowser !== false}
                      onChange={(e) => setNewsletterData({ ...newsletterData, showReadInBrowser: e.target.checked })}
                      className="rounded border-slate-300"
                    />
                    Show "Read in Browser" link
                  </label>
                  <span className="text-xs text-slate-400">(adds link at top of email)</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Quick Preview Toggle */}
              <Button
                variant={previewMode ? 'default' : 'outline'}
                onClick={() => setPreviewMode(!previewMode)}
                data-testid="preview-toggle"
              >
                {previewMode ? (
                  <>
                    <EyeOff size={18} className="mr-2" />
                    Edit
                  </>
                ) : (
                  <>
                    <Eye size={18} className="mr-2" />
                    Preview
                  </>
                )}
              </Button>

              {/* Viewport switcher (preview mode only). Constrains the
                  inline preview pane's width to common email-client widths. */}
              {previewMode && (
                <div className="inline-flex items-center rounded-md border border-slate-300 bg-white p-1" data-testid="newsletter-viewport-switcher">
                  <button type="button" onClick={() => setPreviewViewport('mobile')} title="Mobile (375 px)" data-testid="newsletter-viewport-mobile"
                    className={`p-1.5 rounded transition-colors ${previewViewport === 'mobile' ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                    <Smartphone size={16} />
                  </button>
                  <button type="button" onClick={() => setPreviewViewport('tablet')} title="Tablet (600 px — common email width)" data-testid="newsletter-viewport-tablet"
                    className={`p-1.5 rounded transition-colors ${previewViewport === 'tablet' ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                    <Tablet size={16} />
                  </button>
                  <button type="button" onClick={() => setPreviewViewport('auto')} title="Auto (full pane)" data-testid="newsletter-viewport-auto"
                    className={`p-1.5 rounded transition-colors ${previewViewport === 'auto' ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                    <Monitor size={16} />
                  </button>
                </div>
              )}

              {/* Full Email Preview */}
              <Button
                variant="outline"
                onClick={openFullPreview}
                data-testid="full-preview-button"
                title="Open email preview in new window"
              >
                <ExternalLink size={18} className="mr-2" />
                Email Preview
              </Button>
              
              {/* Send Test Email - only show for saved newsletters */}
              {!isNew && (
                <Button
                  variant="outline"
                  onClick={() => setShowTestEmailDialog(true)}
                  data-testid="test-email-button"
                  title="Send a test email to yourself"
                >
                  <MailCheck size={18} className="mr-2" />
                  Test Email
                </Button>
              )}

              <Button
                onClick={handleSave}
                disabled={saving}
                variant="outline"
                data-testid="save-button"
              >
                <Save size={18} className="mr-2" />
                {saving ? 'Saving...' : 'Save'}
              </Button>
              
              {!isNew && (
                <Button
                  onClick={openSendDialog}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="send-button"
                >
                  <Send size={18} className="mr-2" />
                  Send
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex">
          {/* Editor */}
          <div className={`${previewMode ? 'w-1/2 border-r border-slate-200' : 'flex-1'} px-8 pb-20 transition-all`}>
            <BlockEditor
              blocks={newsletterData.blocks || []}
              onChange={(blocks) => setNewsletterData({ ...newsletterData, blocks })}
              pages={[]}
            />
          </div>

          {/* Quick Preview Panel */}
          {previewMode && (
            <div className="w-1/2 bg-slate-100 p-6 overflow-auto" style={{ maxHeight: 'calc(100vh - 80px)' }}>
              {(() => {
                // Constrain the visible width of the email preview based on
                // the chosen viewport. Email clients render fixed-width HTML
                // tables so width is the dominant variable — no need for an
                // iframe here. 'auto' falls back to the existing max-w-lg.
                const containerWidth = previewViewport === 'mobile' ? 375
                  : previewViewport === 'tablet' ? 600
                  : null;
                const containerClass = containerWidth ? '' : 'max-w-lg';
                const containerStyle = containerWidth
                  ? { width: `${containerWidth}px`, maxWidth: '100%' }
                  : {};
                return (
                  <div className={`${containerClass} mx-auto`} style={containerStyle} data-testid={`newsletter-preview-${previewViewport}`}>
                    {/* Email Preview Header */}
                    <div className="bg-slate-200 rounded-t-lg p-3 flex items-center gap-2">
                      <div className="flex gap-1.5">
                        <div className="w-3 h-3 rounded-full bg-red-400"></div>
                        <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                        <div className="w-3 h-3 rounded-full bg-green-400"></div>
                      </div>
                      <span className="text-xs text-slate-500 ml-2">
                        Email Preview{containerWidth ? ` · ${containerWidth} px` : ''}
                      </span>
                    </div>

                    {/* Email Content */}
                    <div className="bg-white rounded-b-lg shadow-lg overflow-hidden">
                      {/* Email Header */}
                      <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white p-6">
                        <p className="text-xs opacity-75 mb-1">Subject</p>
                        <h2 className="text-lg font-semibold">{newsletterData.subject || 'No subject'}</h2>
                      </div>

                      {/* Email Body */}
                      <div className="p-6">
                        {(!newsletterData.blocks || newsletterData.blocks.length === 0) ? (
                          <p className="text-slate-400 text-center py-8">Add content blocks to see preview</p>
                        ) : (
                          <PreviewRenderer
                            blocks={newsletterData.blocks}
                            isQuickPreview={true}
                            showNavigation={false}
                          />
                        )}
                      </div>

                      {/* Email Footer */}
                      <div className="bg-slate-50 border-t border-slate-200 p-4 text-center">
                        <p className="text-xs text-slate-400">
                          Preview of how your newsletter will appear in email clients
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </div>

      {/* Send Dialog */}
      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="send-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Send size={18} className="text-emerald-600" />
              Send Newsletter
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div className="bg-slate-50 rounded-lg p-2">
              <p className="text-xs text-slate-600">
                <strong>Subject:</strong> {newsletterData.subject || 'No subject'}
              </p>
            </div>
            
            {/* QUICK ACTION - Select All Subscribers - TOP OF DIALOG */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg p-4 shadow-md">
              <div className="flex items-center justify-between">
                <div className="text-white">
                  <p className="font-semibold text-sm">Quick Action</p>
                  <p className="text-blue-100 text-xs">Send to all your subscribers at once</p>
                </div>
                <button
                  type="button"
                  data-testid="quick-select-all-btn"
                  onClick={async () => {
                    try {
                      const response = await api.get('/subscribers?page=1&per_page=10000');
                      const allActive = (response.data.subscribers || [])
                        .filter(s => s.status === 'active')
                        .map(s => s.subscriber_id);
                      setSendToSubscribers(allActive);
                      toast.success(`Selected all ${allActive.length} subscribers!`);
                    } catch (error) {
                      toast.error('Failed to select all');
                    }
                  }}
                  className="bg-white text-blue-700 font-bold px-6 py-3 rounded-lg hover:bg-blue-50 transition-all shadow-sm flex items-center gap-2"
                >
                  <CheckCircle2 size={20} />
                  SELECT ALL ({subscribers.length || 0})
                </button>
              </div>
              {sendToSubscribers.length > 0 && (
                <div className="mt-2 pt-2 border-t border-blue-500 flex items-center justify-between">
                  <span className="text-white text-sm">{sendToSubscribers.length} subscribers selected</span>
                  <button
                    type="button"
                    onClick={() => setSendToSubscribers([])}
                    className="text-blue-200 hover:text-white text-sm underline"
                  >
                    Clear selection
                  </button>
                </div>
              )}
            </div>
            
            {/* Recipients Summary - Compact */}
            {(sendToGroups.length > 0 || sendToSubscribers.length > 0 || recipientEmails.trim()) && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2">
                <p className="text-xs font-medium text-emerald-800 flex items-center gap-1 mb-1">
                  <Users size={12} />
                  Selected: {sendToGroups.length} groups, {sendToSubscribers.length} subscribers
                </p>
                <div className="flex flex-wrap gap-1 max-h-12 overflow-y-auto">
                  {sendToGroups.map(gid => {
                    const group = groups.find(g => g.group_id === gid);
                    return group && (
                      <span key={gid} className="px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded text-[10px] flex items-center gap-0.5">
                        {group.name} ({group.subscriber_count || 0})
                        <button onClick={() => setSendToGroups(sendToGroups.filter(id => id !== gid))} className="ml-0.5 hover:text-indigo-900">×</button>
                      </span>
                    );
                  })}
                  {sendToSubscribers.slice(0, 3).map(subId => {
                    const sub = subscribers.find(s => s.subscriber_id === subId);
                    return sub && (
                      <span key={subId} className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] flex items-center gap-0.5">
                        {sub.email.split('@')[0]}@...
                        <button onClick={() => setSendToSubscribers(sendToSubscribers.filter(id => id !== subId))} className="ml-0.5 hover:text-blue-900">×</button>
                      </span>
                    );
                  })}
                  {sendToSubscribers.length > 3 && (
                    <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded text-[10px]">+{sendToSubscribers.length - 3} more</span>
                  )}
                </div>
              </div>
            )}
            
            {/* Groups Section - Compact */}
            <div className="space-y-1">
              <Label className="flex items-center gap-2 text-sm">
                <Tag size={14} className="text-indigo-600" />
                Send to Groups
              </Label>
              {groups.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {groups.map(group => (
                    <button
                      key={group.group_id}
                      type="button"
                      className={`px-2.5 py-1 rounded-lg text-xs border-2 transition-all ${
                        sendToGroups.includes(group.group_id)
                          ? 'bg-indigo-100 border-indigo-400 text-indigo-700 font-medium'
                          : 'bg-white border-slate-200 text-slate-600 hover:border-indigo-300 hover:bg-indigo-50'
                      }`}
                      onClick={() => {
                        const ids = sendToGroups.includes(group.group_id)
                          ? sendToGroups.filter(id => id !== group.group_id)
                          : [...sendToGroups, group.group_id];
                        setSendToGroups(ids);
                      }}
                    >
                      {sendToGroups.includes(group.group_id) && <CheckCircle2 size={12} className="inline mr-1" />}
                      {group.name} <span className="text-slate-400 ml-1">({group.subscriber_count || 0})</span>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-500 italic">No groups created yet.</p>
              )}
            </div>
            
            {/* Individual Subscribers Section - Compact */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-sm">
                <Users size={14} className="text-blue-600" />
                Or Select Individual Subscribers
                {sendToSubscribers.length > 0 && <span className="text-xs text-blue-600">({sendToSubscribers.length} selected)</span>}
              </Label>
              
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={14} />
                <Input
                  placeholder="Search..."
                  value={subscriberSearchSend}
                  onChange={(e) => setSubscriberSearchSend(e.target.value)}
                  className="pl-8 h-8 text-sm"
                />
              </div>
              {subscribers.length > 0 ? (
                <div className="border rounded-lg max-h-28 overflow-y-auto">
                  {subscribers
                    .filter(s => s.status === 'active')
                    .filter(s => 
                      !subscriberSearchSend || 
                      s.email.toLowerCase().includes(subscriberSearchSend.toLowerCase()) ||
                      (s.name || '').toLowerCase().includes(subscriberSearchSend.toLowerCase())
                    )
                    .slice(0, 50)
                    .map(sub => (
                      <div 
                        key={sub.subscriber_id}
                        className={`flex items-center justify-between px-2 py-1.5 hover:bg-slate-50 cursor-pointer border-b last:border-b-0 ${
                          sendToSubscribers.includes(sub.subscriber_id) ? 'bg-blue-50' : ''
                        }`}
                        onClick={() => {
                          const ids = sendToSubscribers.includes(sub.subscriber_id)
                            ? sendToSubscribers.filter(id => id !== sub.subscriber_id)
                            : [...sendToSubscribers, sub.subscriber_id];
                          setSendToSubscribers(ids);
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <Checkbox checked={sendToSubscribers.includes(sub.subscriber_id)} className="pointer-events-none h-4 w-4" />
                          <p className="text-xs font-medium">{sub.email}</p>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-xs text-slate-500 italic">No subscribers yet.</p>
              )}
            </div>
            
            {/* Custom Email Addresses - Compact */}
            <div className="space-y-1">
              <Label className="flex items-center gap-2 text-sm">
                <Mail size={14} className="text-amber-600" />
                Additional Emails
              </Label>
              <Textarea
                value={recipientEmails}
                onChange={(e) => setRecipientEmails(e.target.value)}
                placeholder="Enter emails (comma or newline separated)"
                className="min-h-[50px] text-sm"
              />
            </div>
            
            {/* Send Mode - Compact */}
            <div className="space-y-2 pt-2 border-t">
              <Label className="text-sm">When to Send</Label>
              <div className="flex gap-2">
                <button
                  type="button"
                  className={`flex-1 px-3 py-2 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                    sendMode === 'now' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() => setSendMode('now')}
                >
                  <Send size={16} />
                  <span className="font-medium text-sm">Send Now</span>
                </button>
                <button
                  type="button"
                  className={`flex-1 px-3 py-2 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                    sendMode === 'schedule' ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() => setSendMode('schedule')}
                >
                  <Calendar size={16} />
                  <span className="font-medium text-sm">Schedule</span>
                </button>
              </div>
              
              {sendMode === 'schedule' && (
                <div className="bg-slate-50 p-3 rounded-lg grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Date</Label>
                    <Input type="date" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} min={new Date().toISOString().split('T')[0]} className="h-9" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Time</Label>
                    <Input type="time" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)} className="h-9" />
                  </div>
                  {scheduleDate && scheduleTime && (
                    <p className="col-span-2 text-xs text-indigo-600 flex items-center gap-1">
                      <Clock size={12} />
                      {new Date(`${scheduleDate}T${scheduleTime}`).toLocaleString()}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleSend} 
              disabled={sending}
              className={sendMode === 'schedule' ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-emerald-600 hover:bg-emerald-700'} 
              data-testid="confirm-send-button"
            >
              {sending ? 'Processing...' : sendMode === 'schedule' ? 'Schedule' : 'Send Newsletter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Test Email Dialog */}
      <Dialog open={showTestEmailDialog} onOpenChange={setShowTestEmailDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MailCheck size={20} className="text-indigo-600" />
              Send Test Email
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-slate-600">
              Send this newsletter to yourself to preview how it will appear in email clients.
            </p>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm text-slate-600">
                <strong>Subject:</strong> {newsletterData.subject || 'No subject'}
              </p>
              <p className="text-sm text-slate-600 mt-1">
                <strong>Content:</strong> {(newsletterData.blocks || []).length} blocks
              </p>
            </div>
            <div className="space-y-2">
              <Label>Your Email Address</Label>
              <Input
                type="email"
                placeholder="your@email.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Test Language</Label>
              <Select value={testLanguage} onValueChange={setTestLanguage}>
                <SelectTrigger data-testid="test-language-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English (Original)</SelectItem>
                  <SelectItem value="fr">French (Français)</SelectItem>
                  <SelectItem value="de">German (Deutsch)</SelectItem>
                  <SelectItem value="es">Spanish (Español)</SelectItem>
                  <SelectItem value="it">Italian (Italiano)</SelectItem>
                  <SelectItem value="pt">Portuguese (Português)</SelectItem>
                  <SelectItem value="nl">Dutch (Nederlands)</SelectItem>
                  <SelectItem value="pl">Polish (Polski)</SelectItem>
                  <SelectItem value="zh">Chinese (中文)</SelectItem>
                  <SelectItem value="ja">Japanese (日本語)</SelectItem>
                  <SelectItem value="ar">Arabic (العربية)</SelectItem>
                  <SelectItem value="ru">Russian (Русский)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                {testLanguage !== 'en' 
                  ? 'Newsletter will be translated before sending (requires Premium translation tier)' 
                  : 'Send in original language'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestEmailDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleSendTestEmail} 
              disabled={sendingTestEmail}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <MailCheck size={16} className="mr-2" />
              {sendingTestEmail ? 'Sending...' : 'Send Test'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NewsletterEditor;
